'use client';

import { useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useRouter } from 'next/navigation';
import Image from 'next/image';

export default function BlogHero() {
  const containerRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const router = useRouter();

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start'],
  });

  const y = useTransform(scrollYProgress, [0, 1], [0, 300]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.2]);
  const textY = useTransform(scrollYProgress, [0, 0.5], [0, 100]);
  const textOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  // Floating elements animation
  const floatingElements = Array.from({ length: 12 }).map((_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: 10 + Math.random() * 30,
    duration: 10 + Math.random() * 20,
    delay: Math.random() * 2,
    shape: Math.random() > 0.7 ? 'square' : 'circle',
    color: Math.random() > 0.5 ? 'primarycolor' : 'secondarycolor',
  }));

  const handleMouseMove = (e) => {
    const { clientX, clientY } = e;
    const { left, top, width, height } =
      containerRef.current.getBoundingClientRect();
    const x = (clientX - left) / width - 0.5;
    const y = (clientY - top) / height - 0.5;
    setMousePosition({ x, y });
  };

  return (
    <motion.div
      ref={containerRef}
      className='relative h-[90vh] overflow-hidden mt-20'
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Background gradient overlay */}
      <div className='absolute inset-0 bg-gradient-to-b from-primarycolor/25 to-transparent z-10 opacity-100' />

      {/* Parallax background image */}
      <motion.div
        className='absolute inset-0 z-0'
        style={{
          y,
          scale,
          rotateX: isHovered ? mousePosition.y * 5 : 0,
          rotateY: isHovered ? mousePosition.x * -5 : 0,
          transition: 'rotateX 0.2s ease-out, rotateY 0.2s ease-out',
        }}
      >
        <Image
          // src='https://images.x-space.ae/assets/images/Aboutus pic/company-pf.png'
          src='https://images.x-space.ae/assets/images/website_image/home_page_6.webp'
          alt='X-Space Real Estate Blog'
          fill
          priority
          className='object-cover'
          sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 40vw'
        />
      </motion.div>

      {/* Floating decorative elements */}
      {floatingElements.map((element) => (
        <motion.div
          key={element.id}
          className={`absolute ${element.shape === 'square' ? 'rounded-lg' : 'rounded-full'} backdrop-blur-sm z-10`}
          style={{
            left: `${element.x}%`,
            top: `${element.y}%`,
            width: element.size,
            height: element.size,
            background:
              element.color === 'primarycolor'
                ? 'rgba(var(--primarycolor-rgb), 0.2)'
                : 'rgba(var(--secondarycolor-rgb), 0.2)',
          }}
          animate={{
            y: [0, -20, 0],
            x: [0, 10, 0],
            rotate: [0, element.shape === 'square' ? 45 : 0, 0],
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.7, 0.3],
          }}
          transition={{
            repeat: Number.POSITIVE_INFINITY,
            duration: element.duration,
            delay: element.delay,
            ease: 'easeInOut',
          }}
        />
      ))}

      {/* Content */}
      <motion.div
        className='absolute inset-0 flex flex-col items-center justify-center text-center px-4 z-20'
        style={{ opacity: textOpacity, y: textY }}
      >
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {/* <motion.h1
            className='text-4xl md:text-6xl lg:text-7xl font-extrabold text-white mb-6 drop-shadow-lg'
            animate={{ scale: isHovered ? 1.05 : 1 }}
            transition={{ duration: 0.3 }}
          >
            <motion.span
              className='bg-clip-text text-secondarycolor bg-gradient-to-r from-white to-gray-300'
              // whileHover={{
              //   backgroundImage:
              //     'linear-gradient(to right, #ffffff, var(--primarycolor))',
              // }}
            >
              Inside X-Space Real Estate
            </motion.span>
          </motion.h1> */}
          <motion.h1 className='text-4xl md:text-6xl lg:text-7xl font-extrabold text-white mb-6 drop-shadow-lg'>
            Inside X-Space Real Estate
          </motion.h1>

          <motion.div
            initial={{ width: 0 }}
            animate={{ width: '100px' }}
            transition={{ duration: 1, delay: 0.8 }}
            className='h-1 bg-gradient-to-r from-primarycolor to-white mx-auto mb-6'
          />

          <motion.p
            className='text-xl md:text-2xl text-white max-w-3xl mx-auto font-bold leading-relaxed drop-shadow-lg'
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            Updates, Insights, and Stories from Our Team as We Shape the Future
            of Real Estate
          </motion.p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
          className='mt-10'
        >
          <motion.button
            className='px-8 py-3 font-medium rounded-full bg-primarycolor hover:bg-secondarycolor text-white hover:text-black transition-all duration-300 transform shadow-lg relative overflow-hidden group'
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => router.push('/services')}
          >
            {/* <motion.span className='absolute inset-0 bg-gradient-to-r from-primarycolor to-secondarycolor opacity-0 group-hover:opacity-100 transition-opacity duration-300' /> */}
            <span className='relative z-10  transition-colors duration-300'>
              Explore Our Stories
            </span>
          </motion.button>
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      {/* <motion.div
        className='absolute bottom-10 left-1/2 transform -translate-x-1/2 z-20'
        animate={{ y: [0, 10, 0] }}
        transition={{ repeat: Number.POSITIVE_INFINITY, duration: 1.5 }}
      >
        <div className='w-8 h-14 border-2 border-white rounded-full flex justify-center items-start p-2 relative overflow-hidden'>
          <motion.div
            className='w-2 h-2 bg-white rounded-full'
            animate={{ y: [0, 16, 0] }}
            transition={{ repeat: Number.POSITIVE_INFINITY, duration: 1.5 }}
          />
          <motion.div
            className='absolute inset-0 bg-gradient-to-b from-primarycolor/0 to-primarycolor/50'
            animate={{ opacity: [0, 0.5, 0] }}
            transition={{ repeat: Number.POSITIVE_INFINITY, duration: 2 }}
          />
        </div>
      </motion.div> */}
    </motion.div>
  );
}
